import { useEffect, useState, type ReactNode } from "react";
import type { SurfaceRecipeId } from "@vjal/screen-ir";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { CoachingAppShell } from "../components/CoachingShell";
import { RouteAccessState, RouteScopeResolvingState } from "../components/AccessState";
import { evaluateRouteAccess } from "../domain/capabilities";
import { canAccessLogsModule, canReadAuditLog } from "../domain/logs-and-exports";
import { prefetchFocusedRouteList } from "../hooks/focused-route-prefetch";
import { useCoachingProfile } from "../hooks/useCoachingQueries";
import { useLiveRecordScopeDecision } from "../hooks/useRecordScopeDecision";
import {
  useWalkReadinessHoldCount,
  useWalkReadyMarker,
  WALK_READINESS_DELEGATING_SCREEN_IDS,
} from "../lib/walk-readiness";
import { ProductRouteRenderProvider } from "../pages/product-route-context";
import { coachingScreenModulesById } from "../screens";
import { getCoachingRoleHint } from "../services/coaching-org-context";
import { selectAuthAwareRoutePath } from "./auth-aware-route-path";
import {
  getEvidenceRoleOverride,
  getRouteDecision,
  isLiveMode,
  normalizeProfileRole,
  shouldResolveLiveRecordScope
} from "./route-decision";
import type { AppRouteDefinition } from "./route-types";

export function CoachingRouteScreen({ route }: { route: AppRouteDefinition | null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profileQuery = useCoachingProfile();
  const evidenceRole = getEvidenceRoleOverride(location.search);
  const profileRole = normalizeProfileRole(profileQuery.data?.role);
  const liveProfileRole = isLiveMode() ? profileRole : null;
  const currentPath = location.pathname === "/" ? "/dashboard" : location.pathname;
  const profileRoleHint = normalizeProfileRole(getCoachingRoleHint());
  const prefetchRole = evidenceRole ?? liveProfileRole ?? profileRoleHint;
  const hintedAccessDecision =
    !evidenceRole && isLiveMode() && profileQuery.isLoading && !liveProfileRole && profileRoleHint && currentPath !== "/login"
      ? evaluateRouteAccess(profileRoleHint, currentPath, false)
      : null;
  const canRenderHintedHistoryGate = Boolean(
    !evidenceRole &&
      isLiveMode() &&
      profileQuery.isLoading &&
      !liveProfileRole &&
      profileRoleHint &&
      currentPath === "/history" &&
      canAccessLogsModule(profileRoleHint) &&
      !canReadAuditLog(profileRoleHint)
  );
  const canRenderHintedRouteDenial = Boolean(hintedAccessDecision && !hintedAccessDecision.allowed);
  const canRenderHintedDenial = canRenderHintedRouteDenial || canRenderHintedHistoryGate;
  const activeRole = evidenceRole ?? liveProfileRole ?? (canRenderHintedDenial ? profileRoleHint : null) ?? "participant";
  const routePath = selectAuthAwareRoutePath({
    currentPath,
    hasEvidenceRole: Boolean(evidenceRole),
    hasLiveProfile: Boolean(liveProfileRole || canRenderHintedDenial)
  });
  // Render-level auth substitution (selectAuthAwareRoutePath) stays
  // authoritative for what is shown; this effect only syncs the URL once a
  // live profile resolves while the address bar still says "/login", so the
  // post-OTP dashboard no longer renders under a stale /login URL.
  useEffect(() => {
    if (location.pathname === "/login" && !evidenceRole && liveProfileRole) {
      navigate("/dashboard", { replace: true });
    }
  }, [location.pathname, evidenceRole, liveProfileRole, navigate]);
  useEffect(() => {
    if (!isLiveMode() || !prefetchRole) return;
    if (!evaluateRouteAccess(prefetchRole, currentPath, profileQuery.data?.isPlatformAdmin === true).allowed) return;
    prefetchFocusedRouteList(queryClient, currentPath);
  }, [currentPath, prefetchRole, profileQuery.data?.orgId, profileQuery.data?.isPlatformAdmin, queryClient]);
  const baseDecision =
    route && routePath === currentPath ? evaluateRouteAccess(activeRole, route, profileQuery.data?.isPlatformAdmin === true) : evaluateRouteAccess(activeRole, routePath, profileQuery.data?.isPlatformAdmin === true)
  const liveRecordScope = useLiveRecordScopeDecision({
    enabled: shouldResolveLiveRecordScope(baseDecision, location.search),
    role: activeRole,
    route: baseDecision.route,
    routePath
  });
  const accessDecision = getRouteDecision(activeRole, routePath, location.search, baseDecision, liveRecordScope.data);
  const hideNavigation = accessDecision.route?.id === "login";
  // While the live `/auth/me` hydration is still in flight the route path
  // falls back to "/login"; show the session interstitial instead of flashing
  // the login shell on cold deep-route loads. Signed-out users settle the
  // query quickly (401 -> error -> isLoading false) and still land on the
  // login surface, and direct "/login" visits render the shell immediately.
  //
  // The session interstitial is COLD-START ONLY. Once the session query has
  // settled at least once inside this mounted screen, a later background
  // refetch (which, for an unauthenticated 401 + staleTime:0, briefly reverts
  // the query to `pending` -> isLoading true) must never swap out the surface
  // again — otherwise an in-progress OTP login (entered email + code-entry
  // step) is unmounted and reset when the user changes browser tab. We latch on
  // the first settle so the OTP flow survives any subsequent refetch. The first
  // settle already renders with isLoading false (interstitial naturally hidden).
  // The latch is a guarded render adjustment (bug-pack class, 2026-07-06): it
  // flips exactly once on the first settled observation and never unsets.
  const [hasSettledSession, setHasSettledSession] = useState(false);
  if (!profileQuery.isLoading && !hasSettledSession) setHasSettledSession(true);
  const isAuthResolving =
    !evidenceRole &&
    isLiveMode() &&
    profileQuery.isLoading &&
    !hasSettledSession &&
    currentPath !== "/login" &&
    !canRenderHintedDenial;
  // While the live record-scope query is still in flight the decision is the
  // deny-by-default "unknown" placeholder; show the branded resolving
  // interstitial instead of denial copy. isLoading is false for disabled
  // queries (mock/evidence modes resolve synchronously) and once a resolver
  // result or error has settled, so denial copy still renders the moment
  // resolution actually denies.
  const isScopeResolving = liveRecordScope.isLoading;
  // Evidence role overrides are synchronous, but platform-admin truth arrives
  // from the profile query. Live walks can also paint a role from the session
  // hint while /auth/me is still in flight (canRenderHintedDenial skips the
  // auth interstitial). Either way, do not publish walk-ready / role chrome
  // before the profile settles, or admin/super_admin shells omit Organisations.
  const needsPlatformAdminChrome = activeRole === "super_admin" || activeRole === "admin";
  const isPlatformAdminChromeResolving = Boolean(
    needsPlatformAdminChrome && profileQuery.isLoading && !profileQuery.data,
  );
  const isResolving = isAuthResolving || isScopeResolving || isPlatformAdminChromeResolving;
  const renderScopedDenialInRouteShell = Boolean(
    !isResolving &&
      !accessDecision.allowed &&
      baseDecision.allowed &&
      accessDecision.route?.scopeKind &&
      accessDecision.route.scopeKind !== "none" &&
      (accessDecision.recordScopeStatus === "out-of-scope" || accessDecision.recordScopeStatus === "unknown")
  );
  const selectedModule = accessDecision.route
    ? coachingScreenModulesById[accessDecision.route.id]
    : undefined;
  const SelectedScreen = selectedModule?.Component;
  const selectedRecipe =
    typeof selectedModule?.ir.recipe === "string" ? (selectedModule.ir.recipe as SurfaceRecipeId) : undefined;

  // Walk-readiness marker, phase 1 (see src/lib/walk-readiness.ts). The shell is
  // TERMINAL the moment auth + record-scope resolution settle: whatever renders
  // then — the screen, an access denial, the scoped denial inside the route
  // shell, or the login surface — is the honest final surface, so the marker
  // flips there. The exception is a delegating screen that WILL mount its own
  // body: its first read is still in flight at shell settle, so it flips itself
  // (phase 2) and the shell stays silent. Delegation is only claimed when that
  // body actually renders — a denied or overridden dashboard has no phase 2, so
  // the shell must still flip or the walk fails `probe_failed`.
  const screenOwnsMarker = Boolean(
    SelectedScreen &&
      accessDecision.allowed &&
      !renderScopedDenialInRouteShell &&
      accessDecision.route &&
      (WALK_READINESS_DELEGATING_SCREEN_IDS[accessDecision.route.id] ||
        (accessDecision.route.id === "meetings" && activeRole === "participant"))
  );
  const walkReadinessHoldCount = useWalkReadinessHoldCount();
  useWalkReadyMarker(!isResolving && !screenOwnsMarker && walkReadinessHoldCount === 0);

  function renderSelectedScreen(stateOverride?: ReactNode) {
    if (!SelectedScreen) return null;
    return (
      <ProductRouteRenderProvider
        value={{
          role: activeRole,
          decision: accessDecision,
          routePath,
          stateOverride,
          recipe: selectedRecipe,
        }}
      >
        <SelectedScreen />
      </ProductRouteRenderProvider>
    );
  }


  return (
    <CoachingAppShell
      activePath={routePath}
      activeRole={activeRole}
      isPlatformAdmin={profileQuery.data?.isPlatformAdmin === true}
      hideNavigation={hideNavigation || isAuthResolving || isPlatformAdminChromeResolving}
    >
      {isResolving ? (
        <RouteScopeResolvingState message={isAuthResolving || isPlatformAdminChromeResolving ? "Checking your session…" : undefined} />
      ) : renderScopedDenialInRouteShell ? null : (
        <RouteAccessState decision={accessDecision} />
      )}
      {renderScopedDenialInRouteShell
        ? renderSelectedScreen(
            <RouteAccessState decision={accessDecision} layout="module" showHeading={false} />,
          )
        : null}
      {accessDecision.allowed && !isResolving ? renderSelectedScreen() : null}
    </CoachingAppShell>
  );
}
